import React from 'react';
import { flushSync } from 'react-dom';
import {
  flexRender,
  Header,
  Row,
  Table as ReactTable,
} from '@tanstack/react-table';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DotsSixVerticalIcon } from '@phosphor-icons/react';
import { useGT } from 'gt-react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

/**
 * Stretches an opt-in row link over its whole row. Module-scoped so the class
 * string never appears as a literal inside JSX (the i18n linter reads those
 * as user-facing copy).
 */
const ROW_LINK_CLASS = 'absolute inset-0 z-[1]';

/**
 * True once the user (or their persisted layout) has set explicit column
 * widths — the signal that flips a resizable table from content-driven auto
 * layout to fixed layout. See the sizing-model comment in DataTable.
 */
function hasCustomColumnSizing<TData>(table: ReactTable<TData>): boolean {
  return Object.keys(table.getState().columnSizing ?? {}).length > 0;
}

interface DataTableProps<TData> {
  table: ReactTable<TData>;
  onRowClick?: (row: TData) => void;
  onRowContextMenu?: (e: React.MouseEvent, row: TData) => void;
  /**
   * Hover/focus signal for rows that navigate. Rows using `onRowClick` navigate
   * programmatically, so TanStack Router's `defaultPreload: 'intent'` never
   * fires for them — this is where a caller preloads the destination route.
   */
  onRowIntent?: (row: TData) => void;
  className?: string;
  isLoading?: boolean;
  emptyMessage?: string;
  emptyState?: React.ReactNode;
  groupHeaderFn?: (row: Row<TData>, index: number) => React.ReactNode | null;
  getRowClassName?: (row: Row<TData>) => string | undefined;
  /**
   * Opt-in: render each row as a real link, so right-click → "Open in new
   * tab", cmd-click and middle-click all work natively while a plain click
   * still navigates in place. Return a TanStack Router `<Link>` and spread
   * the supplied `className`, which stretches it over the whole row.
   *
   * The link overlays the cell content; a cell with its own clickable control
   * must give that control `relative z-10` to rise above it. When set, the
   * link owns navigation — don't also pass `onRowClick` to navigate.
   * Mirrors `GentlyTable`'s prop of the same name.
   */
  renderRowLink?: (row: TData, className: string) => React.ReactNode;
  /**
   * Adds a drag grip to each header for column reordering. The new order is
   * written through the table's `onColumnOrderChange`, so pair this with
   * `columnOrder` state on the owner (e.g. `useTableColumnSettings`).
   * Utility columns that declare `enableHiding: false` (selection checkboxes,
   * row actions) are treated as pinned and get no grip.
   *
   * Column *resizing* needs no prop here: set `enableColumnResizing: true`
   * (+ `columnResizeMode: 'onChange'`) on the table and the resize handles
   * render automatically.
   */
  enableColumnReorder?: boolean;
}

export function DataTable<TData>({
  table,
  onRowClick,
  onRowContextMenu,
  onRowIntent,
  className,
  isLoading = false,
  emptyMessage = 'No results.',
  emptyState,
  groupHeaderFn,
  getRowClassName,
  renderRowLink,
  enableColumnReorder = false,
}: DataTableProps<TData>) {
  // TanStack Table hands back one stable `table` instance whose state it mutates
  // in place. The React Compiler assumes props/values are immutable, so it would
  // memoize the header/body JSX below keyed on that stable `table` reference and
  // compute it exactly once — freezing anything derived from live table state,
  // most visibly the sort-direction icon (`column.getIsSorted()`) in each column
  // header. Opt this renderer out of compiler memoization so it recomputes on
  // every render (which the owner triggers whenever the table state changes).
  // The whole render chain that reads the mutable table (this component, the
  // column header, and the sort icon) must opt out for the icon to track the
  // sort — see DataTableColumnHeader.
  'use no memo';

  const columns = table.getAllColumns();

  // Distance activation so plain clicks on the grip never start a drag.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    // Keyboard reordering: focus a grip, press Space/Enter to pick the column
    // up, arrows to move it, Space/Enter to drop.
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Pinned utility columns (enableHiding: false) are excluded, and the set is
  // derived from the visible leaf columns so it always matches the rendered
  // headers.
  const draggableHeaderIds: string[] = [];
  for (const column of table.getVisibleLeafColumns()) {
    if (column.getCanHide()) draggableHeaderIds.push(column.id);
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    // An empty columnOrder means "definition order" — materialize it so the
    // first drag has real indices to move.
    const state = table.getState().columnOrder ?? [];
    const current = state.length
      ? [...state]
      : table.getAllLeafColumns().map((column) => column.id);
    // Reorder only the draggable slots and splice them back into place:
    // arrayMove over the full order would shift a pinned (or hidden) column
    // sitting between the drag's endpoints out of its position.
    const draggableIds = new Set(draggableHeaderIds);
    const movable = current.filter((id) => draggableIds.has(id));
    const from = movable.indexOf(String(active.id));
    const to = movable.indexOf(String(over.id));
    if (from === -1 || to === -1) return;
    const reordered = arrayMove(movable, from, to);
    let slot = 0;
    table.setColumnOrder(
      current.map((id) => (draggableIds.has(id) ? reordered[slot++] : id)),
    );
  };

  // Sizing model for resizable tables, in two phases:
  //
  // 1. No custom sizing yet (nothing persisted, nothing dragged): the
  //    browser's auto table layout sizes columns to their content — the
  //    sensible default, with the column `size` hints as guidance.
  // 2. The moment a resize drag starts, ColumnResizeHandle snapshots every
  //    rendered column width into columnSizing and the table flips to a
  //    fixed layout with an explicit width (the sum of the column sizes).
  //    The flip is pixel-identical, and from then on a drag moves only the
  //    dragged edge — auto layout would redistribute the total so columns
  //    appear to grow from both sides — and a wide column set overflows into
  //    horizontal scroll (the outer div) rather than squishing, which also
  //    helps low-resolution windows. minWidth keeps a narrow column set
  //    filling the pane; the width-less filler cell absorbs that slack
  //    (fixed layout gives leftover space to columns without a width), so
  //    the sized columns hold their exact pixel widths and the drag tracks
  //    the cursor at every table width. Cells clip overflow in this phase,
  //    since content no longer influences its column's width.
  const isResizable = table.options.enableColumnResizing === true;
  const useFixedLayout = isResizable && hasCustomColumnSizing(table);

  // Loading/empty/group rows must also span the trailing filler cell that
  // fixed-layout header and data rows render.
  const fullRowColSpan = columns.length + (useFixedLayout ? 1 : 0);

  const headerRows = table.getHeaderGroups().map((headerGroup) => (
    <TableRow key={headerGroup.id}>
      {enableColumnReorder ? (
        <SortableContext
          items={draggableHeaderIds}
          strategy={horizontalListSortingStrategy}
        >
          {headerGroup.headers.map((header) =>
            header.column.getCanHide() ? (
              <DraggableTableHead key={header.id} header={header} />
            ) : (
              <StaticTableHead key={header.id} header={header} />
            ),
          )}
        </SortableContext>
      ) : (
        headerGroup.headers.map((header) => (
          <StaticTableHead key={header.id} header={header} />
        ))
      )}
      {useFixedLayout && <TableHead aria-hidden className="p-0" />}
    </TableRow>
  ));

  const tableContent = (
    <Table
      className={cn(useFixedLayout && 'table-fixed')}
      style={
        useFixedLayout
          ? { width: table.getTotalSize(), minWidth: '100%' }
          : undefined
      }
    >
      <TableHeader sticky>{headerRows}</TableHeader>
      <TableBody>
        {isLoading ? (
          <TableRow>
            <TableCell colSpan={fullRowColSpan} className="h-24 text-center">
              Loading...
            </TableCell>
          </TableRow>
        ) : table.getRowModel().rows?.length ? (
          table.getRowModel().rows.map((row, index) => {
            const groupHeader = groupHeaderFn?.(row, index) ?? null;
            return (
              <React.Fragment key={row.id}>
                {groupHeader !== null && (
                  <TableRow className="bg-muted hover:bg-muted border-t-2 border-t-border">
                    <TableCell colSpan={fullRowColSpan} className="py-2">
                      {groupHeader}
                    </TableCell>
                  </TableRow>
                )}
                <TableRow
                  // Stable DOM handle for the row (TanStack row id, i.e. the
                  // owner's getRowId) so callers can scroll/focus a specific
                  // row after a remount — e.g. restoring list context when a
                  // detail pane closes.
                  data-row-id={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  onClick={() => onRowClick?.(row.original)}
                  onMouseEnter={
                    onRowIntent ? () => onRowIntent(row.original) : undefined
                  }
                  onFocus={
                    onRowIntent ? () => onRowIntent(row.original) : undefined
                  }
                  onContextMenu={
                    onRowContextMenu
                      ? (e) => {
                          e.preventDefault();
                          onRowContextMenu(e, row.original);
                        }
                      : undefined
                  }
                  className={cn(
                    (onRowClick || renderRowLink) && 'cursor-pointer',
                    // The stretched link positions against the row. Note this
                    // must not promote descendant anchors, or it would
                    // override the link's own `absolute`.
                    renderRowLink && 'relative',
                    getRowClassName?.(row),
                  )}
                >
                  {row.getVisibleCells().map((cell, cellIndex) => (
                    <TableCell
                      key={cell.id}
                      className={cn(
                        // The row link is an overlay anchored to the row, so
                        // clipping its host cell would crop it to that one
                        // column. Every other cell still clips.
                        useFixedLayout &&
                          !(cellIndex === 0 && renderRowLink) &&
                          'overflow-hidden',
                      )}
                    >
                      {cellIndex === 0 &&
                        renderRowLink?.(row.original, ROW_LINK_CLASS)}
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                  {useFixedLayout && <TableCell aria-hidden className="p-0" />}
                </TableRow>
              </React.Fragment>
            );
          })
        ) : (
          <TableRow>
            <TableCell colSpan={fullRowColSpan} className="h-24 text-center">
              {emptyState ?? emptyMessage}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );

  return (
    <div
      className={cn(
        'rounded-md border overflow-auto',
        // A persistent scrollbar, because overlay scrollbars (macOS default)
        // stay hidden until the user scrolls — leaving no hint that a wide
        // column set has pushed data off-screen. Styling the WebKit scrollbar
        // opts the element out of overlay mode, so a bar is drawn whenever
        // there is overflow; Firefox can't be opted out, so it just gets a
        // matching thin themed bar.
        '[scrollbar-width:thin] [scrollbar-color:var(--border)_transparent]',
        '[&::-webkit-scrollbar]:h-2.5 [&::-webkit-scrollbar]:w-2.5',
        '[&::-webkit-scrollbar-track]:bg-transparent',
        '[&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-border',
        '[&::-webkit-scrollbar-thumb:hover]:bg-muted-foreground',
        className,
      )}
    >
      {enableColumnReorder ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          {tableContent}
        </DndContext>
      ) : (
        tableContent
      )}
    </div>
  );
}

/**
 * Drag handle to reorder a resizable column's right edge. Rendered whenever
 * the table enables column resizing (`header.column.getCanResize()`); the
 * width streams through `columnSizing` state while dragging.
 */
function ColumnResizeHandle<TData>({
  header,
}: {
  header: Header<TData, unknown>;
}) {
  'use no memo';
  if (!header.column.getCanResize()) return null;

  // First-ever resize: the table is still in content-driven auto layout, so
  // snapshot every rendered header width into columnSizing before starting
  // the drag. flushSync applies the snapshot immediately so TanStack's
  // resize handler reads the on-screen width as the drag's starting point —
  // the flip to fixed layout is pixel-identical instead of snapping every
  // column to its default size.
  const beginResize = (event: React.MouseEvent | React.TouchEvent) => {
    const { table } = header.getContext();
    if (!hasCustomColumnSizing(table)) {
      const tableEl = (event.currentTarget as HTMLElement).closest('table');
      const snapshot: Record<string, number> = {};
      tableEl
        ?.querySelectorAll<HTMLTableCellElement>('th[data-column-id]')
        .forEach((th) => {
          const id = th.dataset.columnId;
          if (id) snapshot[id] = Math.round(th.getBoundingClientRect().width);
        });
      if (Object.keys(snapshot).length > 0) {
        flushSync(() => table.setColumnSizing(snapshot));
      }
    }
    header.getResizeHandler()(event);
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      onMouseDown={beginResize}
      onTouchStart={beginResize}
      onClick={(event) => event.stopPropagation()}
      className={cn(
        'absolute right-0 top-0 h-full w-1.5 cursor-col-resize touch-none select-none',
        // Always-visible tick marking the resize boundary, drawn as a
        // centered 1px line inside the wider (invisible) hit area; it grows
        // to a full-height primary line on hover and while dragging.
        "after:absolute after:right-[2px] after:top-1/2 after:w-px after:-translate-y-1/2 after:content-['']",
        header.column.getIsResizing()
          ? 'after:h-full after:bg-primary'
          : 'after:h-4 after:bg-border hover:after:h-full hover:after:bg-primary/60',
      )}
    />
  );
}

function StaticTableHead<TData>({
  header,
}: {
  header: Header<TData, unknown>;
}) {
  'use no memo';
  return (
    <TableHead
      data-column-id={header.column.id}
      style={{ width: header.getSize() }}
      className={cn(
        header.column.getCanResize() && 'relative',
        hasCustomColumnSizing(header.getContext().table) && 'overflow-hidden',
      )}
    >
      {header.isPlaceholder
        ? null
        : flexRender(header.column.columnDef.header, header.getContext())}
      <ColumnResizeHandle header={header} />
    </TableHead>
  );
}

/**
 * Header cell with a drag grip for column reordering. The grip is a separate
 * button so sorting clicks (the header title) and resizing (the right-edge
 * handle) keep working unchanged next to it.
 */
function DraggableTableHead<TData>({
  header,
}: {
  header: Header<TData, unknown>;
}) {
  'use no memo';
  const gt = useGT();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: header.column.id });

  return (
    <TableHead
      ref={setNodeRef}
      data-column-id={header.column.id}
      style={{
        width: header.getSize(),
        // Translate only — the sortable strategy's transform carries a scale
        // component sized against the displaced neighbor, which visibly warps
        // the dragged header when column widths differ.
        transform: CSS.Translate.toString(transform),
        transition,
      }}
      className={cn(
        'relative',
        hasCustomColumnSizing(header.getContext().table) && 'overflow-hidden',
        isDragging && 'z-10 opacity-60',
      )}
    >
      <div className="flex items-center gap-1">
        <button
          type="button"
          className="cursor-grab touch-none text-muted-foreground active:cursor-grabbing"
          {...attributes}
          {...listeners}
          aria-label={gt('Reorder column', {
            context: 'drag grip on a table column header',
          })}
        >
          <DotsSixVerticalIcon className="h-3.5 w-3.5" />
        </button>
        <div className="min-w-0 flex-1">
          {header.isPlaceholder
            ? null
            : flexRender(header.column.columnDef.header, header.getContext())}
        </div>
      </div>
      <ColumnResizeHandle header={header} />
    </TableHead>
  );
}
